import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaPlus, FaTrash, FaSave, FaRobot, FaTimes, FaCopy } from 'react-icons/fa';
import PageHeader from '../../components/PageHeader';
import Select from '../../components/Select';
import DraftBanner from '../../components/DraftBanner';
import { useDraft } from '../../hooks/useDraft';
import { toastError, toastSuccess } from '../../utils/toast';
import { agentTypesService, qaProfilesService, slugKey } from '../../services/agentTypesService';
import ModulesPicker from '../Proyects/AddProyect/PermisosStep/ModulesPicker';
import ClientMenuPreview from '../Proyects/AddProyect/PermisosStep/ClientMenuPreview';
import { getModuleConfigById } from '../../utils/moduleConfigs';

// Tipos de agente: qué hace cada tipo, con qué se mide (perfil de evaluación: objetivo,
// etapas del embudo y motivos de pérdida) y con qué módulos nace un proyecto nuevo.
// Agregar un tipo nuevo no requiere código.

const C = {
  panel: '#12132a', raised: '#171831', line: 'rgba(255,255,255,0.07)', text: '#E8EAF0',
  muted: '#8A90AD', faint: '#5A6080', input: '#1a1b30', cyan: '#00C2FF', red: '#DC1F26',
};
const inputCls = 'w-full rounded-xl px-3 py-2.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF]';
const inputStyle = { backgroundColor: C.input, border: `1px solid ${C.line}`, color: C.text };

const EMPTY_PROFILE = {
  key: '', name: '', objective: '', interest_label: 'Tema de interés',
  stages: [{ key: '', label: '', description: '' }, { key: '', label: '', description: '' }],
  loss_reasons: ['Dejó de responder', 'Otro'],
};

function Field({ id, label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium" style={{ color: C.text }}>{label}</label>
      {children}
      {hint && <p className="text-xs" style={{ color: C.faint }}>{hint}</p>}
    </div>
  );
}

function Block({ title, intro, children }) {
  return (
    <section className="pt-7 border-t first:pt-0 first:border-t-0 space-y-4" style={{ borderColor: C.line }}>
      <div>
        <h3 className="text-base font-semibold" style={{ color: C.text }}>{title}</h3>
        {intro && <p className="text-[13px] mt-1 max-w-[68ch]" style={{ color: C.muted }}>{intro}</p>}
      </div>
      {children}
    </section>
  );
}

/** Editor de un tipo de agente (se monta de nuevo al cambiar de tipo: su borrador es propio). */
function AgentTypeEditor({ type, profiles, types, onSaved, onCancel }) {
  const initial = useMemo(() => {
    const profile = profiles.find((p) => p.key === type?.qa_profile);
    return {
      id: type?.id ?? null,
      name: type?.name ?? '',
      description: type?.description ?? '',
      profileKey: type?.qa_profile ?? '',
      profile: profile ? JSON.parse(JSON.stringify(profile)) : null,
      newProfile: false,
      customModules: !!type?.default_modules,
      modules: type?.default_modules ?? getModuleConfigById(type?.id ?? 0),
    };
  }, [type, profiles]);

  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const draft = useDraft(`agent-type:${type?.id ?? 'new'}`, form, setForm, { ready: true });
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setProfile = (patch) => setForm((f) => ({ ...f, profile: { ...f.profile, ...patch } }));

  const sharedWith = types.filter((t) => t.qa_profile === form.profileKey && t.id !== form.id);

  const chooseProfile = (key) => {
    const p = profiles.find((x) => x.key === key);
    set({ profileKey: key, profile: p ? JSON.parse(JSON.stringify(p)) : null, newProfile: false });
  };

  const startNewProfile = (copyFrom) => {
    const base = copyFrom ? JSON.parse(JSON.stringify(copyFrom)) : { ...EMPTY_PROFILE };
    set({
      newProfile: true,
      profileKey: '',
      profile: { ...base, key: '', name: copyFrom ? `${form.name || copyFrom.name}` : form.name },
    });
  };

  const setStage = (i, field, value) =>
    setProfile({ stages: form.profile.stages.map((s, j) => (j === i ? { ...s, [field]: value } : s)) });

  const validate = () => {
    if (!form.name.trim()) return 'Ponle nombre al tipo de agente';
    const p = form.profile;
    if (!p) return 'Elige cómo se mide este agente';
    if (!p.name.trim() || !p.objective.trim()) return 'El perfil necesita nombre y objetivo';
    if (p.stages.length < 2 || p.stages.length > 8) return 'El embudo necesita entre 2 y 8 etapas';
    if (p.stages.some((s) => !s.label.trim() || !s.description.trim())) return 'Cada etapa necesita nombre y qué tiene que pasar para llegar a ella';
    if (p.loss_reasons.filter((r) => r.trim()).length === 0) return 'Agrega al menos un motivo de pérdida';
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { toastError(err); return; }
    setSaving(true);
    try {
      const p = form.profile;
      const usedKeys = new Set();
      const stages = p.stages.map((s, i) => {
        let key = s.key || slugKey(s.label, [...usedKeys]);
        while (usedKeys.has(key)) key = `${key}_${i + 1}`;
        usedKeys.add(key);
        return { key, label: s.label.trim(), description: s.description.trim() };
      });
      const key = form.newProfile ? slugKey(p.name, profiles.map((x) => x.key)) : form.profileKey;
      const savedProfile = await qaProfilesService.save({
        ...p, key, stages, loss_reasons: p.loss_reasons.map((r) => r.trim()).filter(Boolean),
      });
      const saved = await agentTypesService.save({
        id: form.id, name: form.name, description: form.description,
        qa_profile: savedProfile.key,
        default_modules: form.customModules ? form.modules : null,
      });
      draft.clear();
      toastSuccess(form.id ? 'Tipo de agente guardado' : 'Tipo de agente creado');
      onSaved(saved.id);
    } catch (e) {
      console.error('Error al guardar tipo de agente:', e);
      toastError('No se pudo guardar el tipo de agente. Revisa que no haya otro con el mismo nombre.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-7">
      <DraftBanner restoredAt={draft.restoredAt} onDiscard={draft.discard} />

      <Block title={form.id ? form.name || 'Tipo de agente' : 'Nuevo tipo de agente'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field id="at-name" label="Nombre">
            <input id="at-name" className={inputCls} style={inputStyle} maxLength={80} value={form.name}
              placeholder="Ej. Venta de cursos" onChange={(e) => set({ name: e.target.value })} />
          </Field>
          <Field id="at-desc" label="Qué hace este agente">
            <input id="at-desc" className={inputCls} style={inputStyle} maxLength={200} value={form.description}
              placeholder="Ej. Informa del curso, resuelve dudas y lleva al cliente al pago" onChange={(e) => set({ description: e.target.value })} />
          </Field>
        </div>
      </Block>

      <Block
        title="Cómo se mide"
        intro="Con este perfil leemos cada conversación de los proyectos de este tipo: hasta qué etapa llegó, si cumplió el objetivo y por qué se perdió. Un proyecto puede usar otro perfil si lo necesita."
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px] max-w-md">
            <Select
              id="at-profile" name="at-profile" label="Perfil de evaluación"
              options={profiles.map((p) => ({ value: p.key, label: p.name }))}
              value={form.newProfile ? '' : form.profileKey}
              onChange={(e) => chooseProfile(e.target.value)}
              placeholder={form.newProfile ? 'Perfil nuevo (sin guardar)' : 'Elige un perfil'}
              isSearchable={false} isClearable={false}
            />
          </div>
          {!form.newProfile && (
            <>
              <button type="button" onClick={() => startNewProfile(form.profile)} disabled={!form.profile}
                className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF]"
                style={{ color: C.cyan, border: `1px solid ${C.line}`, opacity: form.profile ? 1 : 0.4 }}>
                <FaCopy aria-hidden /> Copiar como perfil nuevo
              </button>
              <button type="button" onClick={() => startNewProfile(null)}
                className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF]"
                style={{ color: C.cyan, border: `1px solid ${C.line}` }}>
                <FaPlus aria-hidden /> Perfil en blanco
              </button>
            </>
          )}
        </div>

        {!form.newProfile && sharedWith.length > 0 && (
          <p className="text-xs rounded-xl px-3 py-2" style={{ backgroundColor: 'rgba(250,204,21,0.07)', border: '1px solid rgba(250,204,21,0.25)', color: '#FDE68A' }}>
            Este perfil también lo usa{sharedWith.length > 1 ? 'n' : ''}: {sharedWith.map((t) => t.name).join(', ')}. Si lo editas, el cambio aplica a {sharedWith.length > 1 ? 'todos' : 'ambos'}. Para cambiarlo solo aquí, usa “Copiar como perfil nuevo”.
          </p>
        )}

        {form.profile && (
          <div className="rounded-2xl p-5 space-y-5" style={{ backgroundColor: C.raised, border: `1px solid ${C.line}` }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field id="pf-name" label="Nombre del perfil">
                <input id="pf-name" className={inputCls} style={inputStyle} maxLength={80} value={form.profile.name}
                  onChange={(e) => setProfile({ name: e.target.value })} />
              </Field>
              <Field id="pf-interest" label="Qué interés registrar" hint="Aparece en Calidad, p. ej. “Curso de interés” o “Desarrollo de interés”.">
                <input id="pf-interest" className={inputCls} style={inputStyle} maxLength={60} value={form.profile.interest_label}
                  onChange={(e) => setProfile({ interest_label: e.target.value })} />
              </Field>
            </div>
            <Field id="pf-objective" label="Objetivo del agente" hint="Una frase. Se usa para decidir si cada conversación cumplió el objetivo.">
              <textarea id="pf-objective" rows={2} className={inputCls} style={inputStyle} maxLength={300} value={form.profile.objective}
                onChange={(e) => setProfile({ objective: e.target.value })} />
            </Field>

            <div className="space-y-2">
              <p className="text-sm font-medium" style={{ color: C.text }}>Etapas del embudo</p>
              <p className="text-xs" style={{ color: C.faint }}>En orden. La última (en verde) es el objetivo cumplido.</p>
              <ol className="space-y-2">
                {form.profile.stages.map((s, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className="mt-2 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
                      style={{ backgroundColor: i === form.profile.stages.length - 1 ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.06)', color: i === form.profile.stages.length - 1 ? '#86EFAC' : C.muted }}>
                      {i + 1}
                    </span>
                    <div className="flex flex-1 flex-col sm:flex-row gap-2">
                      <input className={`${inputCls} sm:w-52`} style={inputStyle} maxLength={60} value={s.label} placeholder="Nombre"
                        aria-label={`Nombre de la etapa ${i + 1}`} onChange={(e) => setStage(i, 'label', e.target.value)} />
                      <input className={inputCls} style={inputStyle} maxLength={200} value={s.description} placeholder="Qué tiene que pasar en la conversación"
                        aria-label={`Descripción de la etapa ${i + 1}`} onChange={(e) => setStage(i, 'description', e.target.value)} />
                    </div>
                    <button type="button" aria-label={`Quitar etapa ${i + 1}`} disabled={form.profile.stages.length <= 2}
                      onClick={() => setProfile({ stages: form.profile.stages.filter((_, j) => j !== i) })}
                      className="mt-1.5 p-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF]"
                      style={{ color: '#F87171', opacity: form.profile.stages.length <= 2 ? 0.3 : 1 }}>
                      <FaTrash aria-hidden />
                    </button>
                  </li>
                ))}
              </ol>
              <button type="button" disabled={form.profile.stages.length >= 8}
                onClick={() => setProfile({ stages: [...form.profile.stages, { key: '', label: '', description: '' }] })}
                className="text-xs flex items-center gap-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF]" style={{ color: C.cyan }}>
                <FaPlus aria-hidden /> Agregar etapa
              </button>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium" style={{ color: C.text }}>Motivos por los que se pierde un cliente</p>
              <p className="text-xs" style={{ color: C.faint }}>La lectura elige uno de estos; así se pueden agrupar en Calidad.</p>
              <ul className="flex flex-wrap gap-2">
                {form.profile.loss_reasons.map((r, i) => (
                  <li key={i} className="flex items-center rounded-lg" style={{ backgroundColor: C.input, border: `1px solid ${C.line}` }}>
                    <input className="bg-transparent px-2.5 py-1.5 text-sm focus:outline-none w-40" style={{ color: C.text }} maxLength={40} value={r}
                      aria-label={`Motivo ${i + 1}`}
                      onChange={(e) => setProfile({ loss_reasons: form.profile.loss_reasons.map((x, j) => (j === i ? e.target.value : x)) })} />
                    <button type="button" aria-label={`Quitar motivo ${r || i + 1}`} className="px-2 py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF] rounded"
                      style={{ color: C.faint }} onClick={() => setProfile({ loss_reasons: form.profile.loss_reasons.filter((_, j) => j !== i) })}>
                      <FaTimes aria-hidden />
                    </button>
                  </li>
                ))}
                {form.profile.loss_reasons.length < 12 && (
                  <li>
                    <button type="button" onClick={() => setProfile({ loss_reasons: [...form.profile.loss_reasons, ''] })}
                      className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF]"
                      style={{ color: C.cyan, border: `1px dashed ${C.line}` }}>
                      <FaPlus aria-hidden /> Motivo
                    </button>
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}
      </Block>

      <Block
        title="Módulos con que nace un proyecto"
        intro="Al crear un proyecto de este tipo, empieza con estos módulos y permisos. Después se pueden cambiar en cada proyecto. No afecta a los proyectos que ya existen."
      >
        <fieldset className="flex flex-wrap gap-2">
          <legend className="sr-only">Origen de los módulos</legend>
          {[{ v: false, l: 'Configuración base del sistema' }, { v: true, l: 'Elegir módulos para este tipo' }].map((o) => (
            <label key={o.l} className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm cursor-pointer"
              style={{ border: `1px solid ${form.customModules === o.v ? 'rgba(0,194,255,0.4)' : C.line}`, color: C.text, backgroundColor: form.customModules === o.v ? 'rgba(0,194,255,0.05)' : 'transparent' }}>
              <input type="radio" name="at-modules" className="accent-[#00C2FF]" checked={form.customModules === o.v} onChange={() => set({ customModules: o.v })} />
              {o.l}
            </label>
          ))}
        </fieldset>
        {form.customModules && (
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_240px] gap-8">
            <ModulesPicker idPrefix="at-mod" modules={form.modules} onChange={(modules) => set({ modules })} />
            <div className="xl:sticky xl:top-24 self-start">
              <ClientMenuPreview modules={form.modules} channels={null} agentTypeName={form.name} profileName={form.profile?.name} />
            </div>
          </div>
        )}
      </Block>

      <div className="sticky bottom-0 -mx-6 px-6 py-4 flex flex-wrap items-center gap-3" style={{ backgroundColor: 'rgba(13,14,30,0.92)', borderTop: `1px solid ${C.line}`, backdropFilter: 'blur(6px)' }}>
        <p className="text-xs flex-1 min-w-[220px]" style={{ color: C.faint }}>
          Los proyectos de este tipo sin perfil propio se medirán con este perfil desde la próxima lectura de conversaciones.
        </p>
        {onCancel && (
          <button type="button" onClick={onCancel} className="rounded-xl px-4 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF]" style={{ color: C.muted }}>
            Cancelar
          </button>
        )}
        <button type="button" onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF]"
          style={{ backgroundColor: C.red, opacity: saving ? 0.6 : 1 }}>
          <FaSave aria-hidden /> {saving ? 'Guardando…' : 'Guardar tipo de agente'}
        </button>
      </div>
    </div>
  );
}

export default function AgentTypesPage() {
  const [types, setTypes] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // id | 'new' | null

  const load = useCallback(async (selectId) => {
    try {
      const [t, p] = await Promise.all([agentTypesService.getAll(), qaProfilesService.getAll()]);
      setTypes(t);
      setProfiles(p);
      setSelected((cur) => selectId ?? cur ?? t[0]?.id ?? 'new');
    } catch (e) {
      console.error(e);
      toastError('No se pudieron cargar los tipos de agente (¿corriste 16_agent_types.sql?)');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const profileName = (key) => profiles.find((p) => p.key === key)?.name;
  const current = selected === 'new' ? null : types.find((t) => t.id === selected);

  return (
    <div>
      <PageHeader title="Tipos de agente" subtitle="Qué hace cada tipo de agente, cómo se mide y con qué módulos nace un proyecto">
        <button type="button" onClick={() => setSelected('new')}
          className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF]"
          style={{ backgroundColor: C.red }}>
          <FaPlus aria-hidden /> Nuevo tipo de agente
        </button>
      </PageHeader>

      {loading ? (
        <p className="text-sm" style={{ color: C.muted }}>Cargando…</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-6 items-start">
          <nav aria-label="Tipos de agente" className="rounded-2xl overflow-hidden lg:sticky lg:top-4" style={{ backgroundColor: C.panel, border: `1px solid ${C.line}` }}>
            <ul>
              {types.map((t, i) => {
                const active = t.id === selected;
                return (
                  <li key={t.id} style={{ borderTop: i ? `1px solid ${C.line}` : 'none' }}>
                    <button type="button" onClick={() => setSelected(t.id)} aria-current={active ? 'true' : undefined}
                      className="w-full text-left px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#00C2FF]"
                      style={{ backgroundColor: active ? 'rgba(0,194,255,0.08)' : 'transparent', boxShadow: active ? `inset 3px 0 0 ${C.cyan}` : 'none' }}>
                      <span className="flex items-center gap-2 text-sm font-medium" style={{ color: C.text }}>
                        <FaRobot aria-hidden style={{ color: active ? C.cyan : C.faint }} /> {t.name}
                      </span>
                      <span className="block text-xs mt-1" style={{ color: C.muted }}>
                        {profileName(t.qa_profile) ? `Se mide como: ${profileName(t.qa_profile)}` : 'Sin perfil de evaluación'}
                      </span>
                      <span className="block text-xs" style={{ color: C.faint }}>
                        {t.projects === 1 ? '1 proyecto' : `${t.projects} proyectos`}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {types.length === 0 && (
              <p className="px-4 py-5 text-sm" style={{ color: C.muted }}>Aún no hay tipos de agente. Crea el primero.</p>
            )}
          </nav>

          <div className="rounded-2xl p-6" style={{ backgroundColor: C.panel, border: `1px solid ${C.line}` }}>
            {selected !== null && (
              <AgentTypeEditor
                key={selected}
                type={current}
                types={types}
                profiles={profiles}
                onSaved={(id) => load(id)}
                onCancel={selected === 'new' ? () => setSelected(types[0]?.id ?? null) : null}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
